import { useEffect, useState } from "react";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine, Legend } from "recharts";
import { ArrowDownCircle, ArrowUpCircle, Wallet, TrendingDown, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, GlassCard, StatCard, fmtMoney } from "@/components/erp";
import api, { errMsg } from "@/lib/api";
import { ErtelemeOneri } from "@/components/ErtelemeOneri";

export const kisa = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("tr-TR", { day: "2-digit", month: "short" });
const kTL = (v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toLocaleString("tr-TR", { maximumFractionDigits: 1 })}B` : v.toLocaleString("tr-TR"));

export const NakitUyari = ({ u, testid = "na-uyari" }) => {
  if (!u || (!u.acik && !u.min_alti)) return null;
  const satir = (x, baslik) => x && (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1"><b className="text-red-200">{baslik}</b>
      <span>{kisa(x.tarih)} tarihinde bakiye {fmtMoney(x.bakiye)}</span><span>· en düşük {kisa(x.en_dusuk_tarih)}</span><span className="font-semibold">· eksik {fmtMoney(x.eksik)}</span></div>
  );
  return (
    <div className="rounded-2xl border border-red-500/40 bg-red-500/10 p-4 mb-6 text-sm text-red-100 flex gap-3" data-testid={testid}>
      <AlertTriangle className="w-5 h-5 text-red-300 shrink-0 mt-0.5" />
      <div className="space-y-1.5">{satir(u.acik, "Nakit açığı: bakiye sıfırın altına düşüyor.")}{satir(u.min_alti, `Minimum sınırın (${fmtMoney(u.min_bakiye)}) altına düşüyor.`)}</div>
    </div>
  );
};

const Ipucu = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const g = payload[0].payload;
  return (
    <div className="glass rounded-xl p-3 text-xs space-y-0.5" style={{ background: "var(--bg-hex)" }}>
      <div className="font-medium mb-1">{kisa(label)}</div>
      <div className="text-emerald-300">Giriş {fmtMoney(g.giris)}</div><div className="text-red-300">Çıkış {fmtMoney(g.cikis)}</div>
      <div>Bakiye {fmtMoney(g.bakiye)}</div>
      {payload.some((p) => p.dataKey === "sonra") && g.sonra !== undefined && <div className="text-lime-300">Erteleme sonrası {fmtMoney(g.sonra)}</div>}
    </div>
  );
};

const Kalemler = ({ baslik, list, renk, testid }) => (
  <div data-testid={testid} className="min-w-0">
    <div className={`text-[11px] uppercase tracking-[0.2em] mb-2 ${renk}`}>{baslik} ({list.length})</div>
    {list.length === 0 ? <div className="text-xs text-muted-foreground">Kayıt yok.</div> : (
      <div className="space-y-1.5">{list.map((x, i) => (
        <div key={i} className="flex justify-between gap-3 text-sm rounded-lg bg-white/[0.04] px-3 py-2">
          <span className="min-w-0 truncate"><span className="text-xs text-muted-foreground mr-2">{x.tur}</span>{x.aciklama}{x.gecikmis && <span className="ml-2 text-[10px] text-amber-300">gecikmiş</span>}</span>
          <span className="font-mono shrink-0">{fmtMoney(x.tutar)}</span>
        </div>
      ))}</div>
    )}
  </div>
);

export default function NakitAkis() {
  const [d, setD] = useState(null);
  const [secili, setSecili] = useState(null);
  const [oneri, setOneri] = useState(null);
  const [goster, setGoster] = useState(false);
  useEffect(() => {
    api.get("/nakit-akis?gun=30").then((r) => { setD(r.data); setSecili(r.data.gunler[0]); }).catch((e) => { toast.error(errMsg(e)); setD(false); });
    api.get("/nakit-akis/erteleme-onerisi?gun=30").then((r) => setOneri(r.data)).catch(() => setOneri(null));
  }, []);
  if (d === false) return <GlassCard className="p-8 text-sm text-muted-foreground" data-testid="na-hata">Nakit akışı yüklenemedi.</GlassCard>;
  if (!d) return <div className="py-20 text-center text-sm text-muted-foreground" data-testid="na-yukleniyor">Yükleniyor…</div>;
  const veri = d.gunler.map((g, i) => ({ ...g, cikisNeg: -g.cikis, sonra: oneri?.sonra?.[i]?.bakiye }));
  const esik = d.uyari?.min_bakiye || 0;
  return (
    <div data-testid="nakit-akis-page">
      <PageHeader eyebrow="Finans" title="Nakit Akışı · 30 gün" desc={`Bugünkü banka + kasa bakiyesinden (${d.hesap_sayisi} aktif hesap) başlayarak beklenen tahsilat ve ödemeler. Gecikmiş kalemler bugüne yazılır.`} />
      <NakitUyari u={d.uyari} />
      <ErtelemeOneri o={oneri} goster={goster} setGoster={setGoster} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testid="na-acilis" label="Bugünkü bakiye" value={fmtMoney(d.acilis_bakiye)} icon={Wallet} />
        <StatCard testid="na-giris" label="Beklenen giriş" value={fmtMoney(d.toplam_giris)} icon={ArrowDownCircle} delay={60} />
        <StatCard testid="na-cikis" label="Beklenen çıkış" value={fmtMoney(d.toplam_cikis)} icon={ArrowUpCircle} delay={120} />
        <StatCard testid="na-en-dusuk" label="En düşük bakiye" value={fmtMoney(d.en_dusuk.bakiye)} hint={kisa(d.en_dusuk.tarih)} icon={TrendingDown} delay={180} />
      </div>
      <GlassCard className="p-4 sm:p-6 mb-6" data-testid="na-grafik">
        <div className="h-[340px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={veri} onClick={(e) => { const g = e?.activePayload?.[0]?.payload || veri.find((x) => x.tarih === e?.activeLabel) || veri[Number(e?.activeTooltipIndex ?? e?.activeIndex)]; if (g) setSecili(g); }} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="tarih" tickFormatter={kisa} tick={{ fill: "#8b93a7", fontSize: 11 }} interval="preserveStartEnd" minTickGap={18} />
              <YAxis yAxisId="akis" tickFormatter={kTL} tick={{ fill: "#8b93a7", fontSize: 11 }} width={52} />
              <YAxis yAxisId="bakiye" orientation="right" tickFormatter={kTL} tick={{ fill: "#8b93a7", fontSize: 11 }} width={52} />
              <Tooltip content={<Ipucu />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
              <Legend formatter={(v) => ({ giris: "Giriş", cikisNeg: "Çıkış", bakiye: "Tahmini bakiye", sonra: "Erteleme sonrası" }[v])} wrapperStyle={{ fontSize: 12 }} />
              <ReferenceLine yAxisId="akis" y={0} stroke="rgba(255,255,255,0.25)" />
              <Bar yAxisId="akis" dataKey="giris" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={18} />
              <Bar yAxisId="akis" dataKey="cikisNeg" fill="#ef4444" radius={[0, 0, 4, 4]} maxBarSize={18} />
              <ReferenceLine yAxisId="bakiye" y={0} stroke="rgba(239,68,68,0.6)" strokeDasharray="4 4" />
              {esik > 0 && <ReferenceLine yAxisId="bakiye" y={esik} stroke="rgba(245,158,11,0.7)" strokeDasharray="6 4" label={{ value: `min ${kTL(esik)}`, fill: "#f59e0b", fontSize: 11, position: "insideTopRight" }} />}
              <Line yAxisId="bakiye" dataKey="bakiye" type="monotone" stroke="var(--g1)" strokeWidth={2.5}
                dot={(p) => (p.payload.bakiye < Math.max(esik, 0) ? <circle key={p.index} cx={p.cx} cy={p.cy} r={4} fill={p.payload.bakiye < 0 ? "#ef4444" : "#f59e0b"} stroke="none" data-testid="na-eksi-nokta" /> : <g key={p.index} />)} />
            {goster && oneri?.oneriler?.length > 0 && <Line yAxisId="bakiye" dataKey="sonra" type="monotone" stroke="#a3e635" strokeWidth={2} strokeDasharray="5 4" dot={false} />}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <p className="text-xs text-muted-foreground mt-2">Bir güne tıklayın, o günün kalemleri aşağıda listelenir. 30 gün sonrası: giriş {fmtMoney(d.donem_sonrasi.giris)} · çıkış {fmtMoney(d.donem_sonrasi.cikis)}.</p>
      </GlassCard>
      {secili && (
        <GlassCard className="p-5" data-testid="na-gun-detay">
          <div className="flex flex-wrap justify-between gap-2 mb-4"><div className="font-head text-lg" data-testid="na-secili-tarih">{kisa(secili.tarih)}</div>
            <div className="text-sm text-muted-foreground">Net <span className={`font-mono ${secili.net < 0 ? "text-red-300" : "text-emerald-300"}`}>{fmtMoney(secili.net)}</span> · gün sonu bakiye <span className="font-mono">{fmtMoney(secili.bakiye)}</span></div></div>
          <div className="grid lg:grid-cols-2 gap-6">
            <Kalemler baslik="Gelecek" list={secili.giris_detay} renk="text-emerald-300" testid="na-giris-detay" />
            <Kalemler baslik="Ödenecek" list={secili.cikis_detay} renk="text-red-300" testid="na-cikis-detay" />
          </div>
        </GlassCard>
      )}
    </div>
  );
}
